import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { build, beatGrid } from '@easyedit/timeline'
import { api } from '../../../lib/api/client'
import { buildInsert } from '../../../lib/timeline/insert'
import type { LibraryAsset, LibraryFacets, LibraryImport, LibraryKind, Project } from '../../../lib/api/types'
import { useEditorStore } from '../../../stores/editorStore'

const TABS: Array<{ kind: LibraryKind; label: string }> = [
  { kind: 'MUSIC', label: 'Trilhas' },
  { kind: 'SFX', label: 'Efeitos' },
  { kind: 'ELEMENT', label: 'Elementos' },
  { kind: 'IMAGE', label: 'Imagens' },
]

/** Quanto uma imagem ou elemento dura quando não tem duração própria. */
const STILL_DURATION = 5

/**
 * Biblioteca de assets.
 *
 * O projeto começa com trilhas, efeitos e elementos gráficos disponíveis — não com uma
 * tela vazia pedindo upload. Tudo aqui é gerado pelo produto, então não há pendência de
 * licença nem crédito a exibir.
 *
 * Inserir passa por duas etapas: importar (cria um MediaAsset apontando para o MESMO
 * arquivo no storage, sem cópia de bytes) e então um comando na timeline. Nada é
 * inserido por um caminho paralelo — a timeline continua sendo a fonte da verdade.
 */
export function LibraryPanel({ project }: { project: Project }) {
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<LibraryKind>('MUSIC')
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const sequence = useEditorStore((s) => s.sequence)
  const dispatch = useEditorStore((s) => s.dispatch)
  const playhead = useEditorStore((s) => s.playhead)

  // Digitação não dispara uma requisição por tecla.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 220)
    return () => clearTimeout(timer)
  }, [term])

  useEffect(() => setCategory(null), [kind])

  const facets = useQuery({
    queryKey: ['library-facets', project.workspaceId],
    queryFn: () => api.get<LibraryFacets>(`/library/facets?workspaceId=${project.workspaceId}`),
    staleTime: 5 * 60_000,
  })

  const assets = useQuery({
    queryKey: ['library', project.workspaceId, kind, category, debounced],
    queryFn: () => {
      const params = new URLSearchParams({ workspaceId: project.workspaceId, kind, limit: '200' })
      if (category) params.set('category', category)
      if (debounced) params.set('q', debounced)
      return api.get<LibraryAsset[]>(`/library?${params.toString()}`)
    },
  })

  const categories = useMemo(
    () => (facets.data?.categories ?? []).filter((c) => c.kind === kind),
    [facets.data, kind],
  )

  async function insert(asset: LibraryAsset) {
    if (!sequence) return
    setBusy(asset.id)
    try {
      const media = await api.post<LibraryImport>(
        `/projects/${project.id}/library/${asset.id}/import`,
        {},
      )

      const itemKind = asset.kind === 'VIDEO' ? 'video' : asset.kind === 'MUSIC' || asset.kind === 'SFX' ? 'audio' : 'image'
      const trackKind = asset.kind === 'MUSIC' ? 'music' : asset.kind === 'SFX' ? 'sfx' : asset.kind === 'VIDEO' ? 'video' : 'overlay'
      const duration = media.durationSec ?? asset.durationSec ?? STILL_DURATION

      const { command, createdTrack } = buildInsert(sequence, {
        assetId: media.id,
        itemKind,
        trackKind,
        start: playhead,
        duration,
        label: asset.name,
        // Trilha entra abaixo da voz: 100% de música cobre a fala. Efeito entra cheio.
        volume: asset.kind === 'MUSIC' ? 0.35 : 1,
        fadeIn: asset.kind === 'MUSIC' ? 1 : 0,
        fadeOut: asset.kind === 'MUSIC' ? 1.5 : 0,
        // Batidas e compassos viajam com o item: é deles que sai o corte na batida, e
        // guardá-los na timeline mantém a informação junto do que ela descreve.
        meta: {
          libraryAssetId: asset.id,
          librarySlug: asset.slug,
          ...(asset.bpm ? { bpm: asset.bpm } : {}),
          ...(asset.beats ? { beats: asset.beats } : {}),
          ...(asset.bars ? { bars: asset.bars } : {}),
        },
      })

      await dispatch(command)
      if (createdTrack) {
        useEditorStore.getState().setError(null)
      }
      void queryClient.invalidateQueries({ queryKey: ['media', project.id] })
    } catch (error) {
      useEditorStore.getState().setError(
        error instanceof Error ? `Não foi possível inserir "${asset.name}": ${error.message}` : 'Falha ao inserir',
      )
    } finally {
      setBusy(null)
    }
  }

  /** Marcadores nas batidas da trilha já inserida — a régua para cortar no ritmo. */
  async function markBeats() {
    if (!sequence) return
    const grid = beatGrid(sequence)
    if (grid.length === 0) {
      useEditorStore.getState().setError('Insira uma trilha com batidas conhecidas antes de marcar o ritmo.')
      return
    }
    const existing = new Set(sequence.markers.map((m) => Math.round(m.time * 1000)))
    const ctx = { sequenceId: sequence.id }
    // Só os pontos fortes: uma marca a cada batida num vídeo de três minutos a 120 BPM
    // seriam 360 marcadores, e a régua viraria uma faixa sólida.
    const commands = grid
      .filter((b) => b.strong && !existing.has(Math.round(b.time * 1000)))
      .map((b, i) => build.addMarker(ctx, b.time, `Compasso ${i + 1}`, '#E8A33D'))
    if (commands.length === 0) {
      useEditorStore.getState().setError('Os compassos desta trilha já estão marcados.')
      return
    }
    await dispatch(build.batch(ctx, commands))
  }

  const hasBeats = sequence ? beatGrid(sequence).some((b) => b.strong) : false

  return (
    <div className="p-3 flex flex-col gap-3">
      <div>
        <h2 className="text-xs font-semibold">Biblioteca</h2>
        <p className="mt-1 text-2xs text-ink-400 leading-relaxed">
          Trilhas, efeitos e elementos prontos. Inserir cria um clip na timeline, editável
          como qualquer outro.
        </p>
      </div>

      <div className="flex gap-0.5 rounded bg-ink-900 p-0.5">
        {TABS.map((tab) => (
          <button
            key={tab.kind}
            onClick={() => setKind(tab.kind)}
            className={`flex-1 rounded py-1 text-2xs transition-colors
              ${kind === tab.kind ? 'bg-ink-750 text-ink-100' : 'text-ink-400 hover:text-ink-200'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <input
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Buscar por nome ou tag…"
        className="w-full rounded border border-ink-750 bg-ink-900 px-2 py-1.5 text-2xs
          text-ink-100 placeholder:text-ink-600 focus:border-accent focus:outline-none"
      />

      {categories.length > 1 && (
        <div className="flex flex-wrap gap-1">
          <Chip active={category === null} onClick={() => setCategory(null)}>
            tudo
          </Chip>
          {categories.map((c) => (
            <Chip key={c.category} active={category === c.category} onClick={() => setCategory(c.category)}>
              {c.category} <span className="text-ink-600 tnum">{c.count}</span>
            </Chip>
          ))}
        </div>
      )}

      {kind === 'MUSIC' && hasBeats && (
        <button
          onClick={() => void markBeats()}
          className="rounded border border-ink-750 py-1.5 text-2xs text-ink-300
            hover:border-accent hover:text-accent transition-colors"
        >
          ♩ Marcar compassos na régua
        </button>
      )}

      <div className="flex flex-col gap-1.5">
        {assets.isLoading && <p className="text-2xs text-ink-500 text-center py-4">Carregando…</p>}
        {assets.data?.length === 0 && (
          <p className="text-2xs text-ink-500 text-center py-4">Nada encontrado com esse filtro.</p>
        )}
        {assets.data?.map((asset) => (
          <LibraryRow
            key={asset.id}
            asset={asset}
            busy={busy === asset.id}
            disabled={!sequence}
            onInsert={() => void insert(asset)}
          />
        ))}
      </div>
    </div>
  )
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-2 py-0.5 text-[10px] transition-colors
        ${active ? 'border-accent text-accent bg-accent-soft/30' : 'border-ink-750 text-ink-400 hover:border-ink-600'}`}
    >
      {children}
    </button>
  )
}

function LibraryRow({
  asset,
  busy,
  disabled,
  onInsert,
}: {
  asset: LibraryAsset
  busy: boolean
  disabled: boolean
  onInsert: () => void
}) {
  const audible = asset.kind === 'MUSIC' || asset.kind === 'SFX'

  return (
    <div className="rounded border border-ink-750 p-2 flex gap-2 items-center hover:border-ink-600 transition-colors">
      <Thumb asset={asset} />

      <div className="min-w-0 flex-1">
        <div className="text-2xs text-ink-200 truncate">{asset.name}</div>
        <div className="font-mono text-[9px] text-ink-500 tnum truncate">
          {asset.durationSec ? `${asset.durationSec.toFixed(1)}s` : ''}
          {asset.bpm ? ` · ${asset.bpm} BPM` : ''}
          {asset.mood ? ` · ${asset.mood}` : ''}
          {!asset.durationSec && asset.width ? `${asset.width}×${asset.height}` : ''}
        </div>
      </div>

      {audible && <PreviewButton asset={asset} />}

      <button
        onClick={onInsert}
        disabled={busy || disabled}
        title={disabled ? 'Abra uma sequência' : 'Inserir no playhead'}
        className="shrink-0 rounded border border-ink-700 px-2 py-1 text-[10px] text-ink-300
          hover:border-accent hover:text-accent disabled:opacity-40 disabled:cursor-default transition-colors"
      >
        {busy ? '…' : '+'}
      </button>
    </div>
  )
}

function Thumb({ asset }: { asset: LibraryAsset }) {
  // O SVG é desenhado como vetor aqui e lido como vetor no render — uma origem, dois
  // consumidores. Rasterizar para a miniatura criaria uma segunda verdade de cor.
  if (asset.kind === 'ELEMENT' && asset.svg) {
    return (
      <div
        className="w-12 h-8 shrink-0 rounded bg-ink-900 overflow-hidden grid place-items-center [&>svg]:w-full [&>svg]:h-full"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: paintPreview(asset.svg) }}
      />
    )
  }
  if (asset.urls?.preview || asset.urls?.file) {
    if (asset.kind === 'IMAGE' || asset.kind === 'VIDEO') {
      return (
        <img
          src={asset.urls.preview ?? asset.urls.file ?? ''}
          alt=""
          className="w-12 h-8 shrink-0 rounded object-cover bg-ink-900"
        />
      )
    }
  }
  return (
    <div className="w-12 h-8 shrink-0 rounded bg-ink-900 grid place-items-center text-2xs text-ink-500">
      {asset.kind === 'MUSIC' ? '♫' : asset.kind === 'SFX' ? '♪' : '◇'}
    </div>
  )
}

/** Cores neutras para a miniatura; a pintura de verdade acontece na importação. */
function paintPreview(svg: string): string {
  return svg
    .replace(/\{\{color\}\}/g, '#E7E9EE')
    .replace(/\{\{color2\}\}/g, '#E8A33D')
    .replace(/\{\{accent\}\}/g, '#4FBFA9')
}

/**
 * Prévia de áudio.
 *
 * Um único elemento por linha, criado sob demanda: montar 21 `<audio>` de uma vez faria
 * o navegador abrir 21 conexões ao storage só para exibir a lista.
 */
function PreviewButton({ asset }: { asset: LibraryAsset }) {
  const ref = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)

  useEffect(() => () => ref.current?.pause(), [])

  function toggle() {
    const url = asset.urls?.file
    if (!url) return
    if (!ref.current) {
      const audio = new Audio(url)
      audio.onended = () => setPlaying(false)
      audio.onpause = () => setPlaying(false)
      ref.current = audio
    }
    if (playing) {
      ref.current.pause()
      ref.current.currentTime = 0
      setPlaying(false)
      return
    }
    // Só uma prévia por vez: duas trilhas simultâneas não dizem nada sobre nenhuma.
    document.querySelectorAll('audio').forEach((el) => el.pause())
    void ref.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false))
  }

  return (
    <button
      onClick={toggle}
      disabled={!asset.urls?.file}
      title="Ouvir"
      className="shrink-0 w-6 h-6 rounded-full border border-ink-700 grid place-items-center
        text-[9px] text-ink-300 hover:border-accent hover:text-accent disabled:opacity-40 transition-colors"
    >
      {playing ? '■' : '▶'}
    </button>
  )
}
